import { LinearGradient } from 'expo-linear-gradient'
import { Box, Pressable, ScrollView, Text, useTheme } from 'native-base'
import React, { useImperativeHandle, forwardRef, useState, useEffect } from 'react'
import { StatusBar, StyleSheet } from 'react-native'
import Animated, {
  interpolate,
  useAnimatedReaction,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
  runOnJS,
} from 'react-native-reanimated'
import { initialWindowMetrics, useSafeAreaInsets } from 'react-native-safe-area-context'

import { Icon } from './Icon'

import { space } from '~constants/theme'
import { useIsFocused, useDeviceInfo } from '~hooks'
import { IconNames } from '~types/icon'
import { noop } from '~utils'

const AnimatedBox = Animated.createAnimatedComponent(Box)
const AnimatedScrollView = Animated.createAnimatedComponent(ScrollView)
const AnimatedPressable = Animated.createAnimatedComponent(Pressable)

type PropsType = {
  BottomComponent?: JSX.Element | null
  changeStatusBarStyle?: boolean
  children: JSX.Element | JSX.Element[]
  headerTitle: string
  iconName?: IconNames
  onPress?: () => void
}

const TOP_EDGE = initialWindowMetrics?.insets?.top || 0
const BUTTON_SIZE = 25
const INITIAL_BUTTON_X = 24
const INITIAL_BUTTON_Y = TOP_EDGE + 35
const SCROLL_THRESHOLD = INITIAL_BUTTON_Y + 50 // manipulate this value to slow down/ speed up the button movement

export const ScrollViewWithAnimatedHeader = forwardRef(
  (
    {
      BottomComponent,
      changeStatusBarStyle = true,
      children,
      headerTitle = '',
      iconName = 'arrow-left-line',
      onPress = noop,
    }: PropsType,
    ref
  ): JSX.Element => {
    const { top } = useSafeAreaInsets()
    const isFocused = useIsFocused()
    const { colors } = useTheme()
    const { isAndroid, isTablet, isWeb } = useDeviceInfo()
    const headerTopPadding = isAndroid ? (isTablet ? 12 : 10) : isTablet && !isWeb ? 9 : 7
    const [hasReachedThreshold, setHasReachedThreshold] = useState(false)
    const scrollOffset = useSharedValue(0)
    const buttonYPosition = useSharedValue(0)

    const thresholdTransition = useDerivedValue(() => {
      return withTiming(hasReachedThreshold ? 1 : 0, { duration: 250 })
    })

    const scrollHandler = useAnimatedScrollHandler({
      onScroll: (event) => {
        scrollOffset.value = event.contentOffset.y
      },
    })

    useImperativeHandle(ref, () => ({
      scrollOffsetValue: scrollOffset,
    }))

    useAnimatedReaction(
      () => {
        return scrollOffset.value
      },
      (data) => {
        if (data < 0) {
          buttonYPosition.value = 0
        } else if (data > SCROLL_THRESHOLD) {
          buttonYPosition.value = SCROLL_THRESHOLD
          !hasReachedThreshold && runOnJS(setHasReachedThreshold)(true)
        } else {
          buttonYPosition.value = data
          hasReachedThreshold && runOnJS(setHasReachedThreshold)(false)
        }
      }
    )

    useEffect(() => {
      if (changeStatusBarStyle) {
        if (isFocused) {
          StatusBar.setBarStyle('light-content')
        } else {
          StatusBar.setBarStyle('dark-content')
        }
      }
    }, [changeStatusBarStyle, isFocused])

    useEffect(() => {
      if (changeStatusBarStyle) {
        if (hasReachedThreshold) {
          StatusBar.setBarStyle('dark-content')
        } else StatusBar.setBarStyle('light-content')
      }
    }, [changeStatusBarStyle, hasReachedThreshold])

    const headerOpacity = useAnimatedStyle(() => ({
      opacity: thresholdTransition.value,
    }))
    const gradientOpacity = useAnimatedStyle(() => ({
      opacity: interpolate(thresholdTransition.value, [0, 1], [1, 0]),
    }))

    const textTransition = useAnimatedStyle(() => ({
      transform: [{ translateY: interpolate(thresholdTransition.value, [0, 1], [10, 0]) }],
    }))
    const buttonStyle = useAnimatedStyle(() => ({
      transform: [
        {
          translateY: interpolate(
            buttonYPosition.value,
            [0, SCROLL_THRESHOLD],
            [INITIAL_BUTTON_Y, TOP_EDGE]
          ),
        },
        {
          scale: interpolate(buttonYPosition.value, [0, SCROLL_THRESHOLD], [1, 0.85]),
        },
        {
          translateX: interpolate(
            buttonYPosition.value,
            [0, SCROLL_THRESHOLD],
            [INITIAL_BUTTON_X, INITIAL_BUTTON_X / 3]
          ),
        },
      ],
    }))

    return (
      <>
        <AnimatedBox
          bgColor="white"
          w="full"
          pt={`${TOP_EDGE ? TOP_EDGE + 2 : headerTopPadding}px`}
          pb="1.5"
          zIndex={100}
          overflow="hidden"
          position="absolute"
          style={headerOpacity}
        >
          <AnimatedBox
            style={textTransition}
            pt="1"
            mx={`${INITIAL_BUTTON_X + BUTTON_SIZE * 0.85}px`}
          >
            <Text bold fontSize="lg" numberOfLines={1} textAlign="center">
              {headerTitle}
            </Text>
          </AnimatedBox>
        </AnimatedBox>
        <AnimatedPressable
          bgColor="white"
          onPress={onPress}
          p="2"
          position="absolute"
          rounded="full"
          style={buttonStyle}
          zIndex={100}
        >
          <Icon color="black" name={iconName} size={BUTTON_SIZE} />
        </AnimatedPressable>
        {changeStatusBarStyle ? (
          <AnimatedBox
            h={`${top || StatusBar.currentHeight}px`}
            w="full"
            zIndex={100}
            position="absolute"
            style={gradientOpacity}
          >
            <LinearGradient
              colors={[colors['gray']['600'], 'transparent']}
              style={[styles.gradientStyle]}
              start={{ x: 0, y: 0 }}
              end={{ x: 0, y: 1 }}
            />
          </AnimatedBox>
        ) : null}
        <AnimatedScrollView
          contentContainerStyle={styles.contentContainerStyle}
          onScroll={scrollHandler}
          scrollEventThrottle={16}
          showsVerticalScrollIndicator={false}
        >
          {children}
        </AnimatedScrollView>
        {BottomComponent}
      </>
    )
  }
)

const styles = StyleSheet.create({
  contentContainerStyle: { flexGrow: 1, paddingBottom: space['8'] },
  gradientStyle: { height: '100%' },
})
